import { useCallback, useEffect, useRef, useState } from "react";

import { getContractAnalytics } from "../services/onboarding/contracts";
import { ContractAnalyticsQuery, SigningAnalytics } from "../types/onboarding";

interface UseContractAnalytics {
  data: SigningAnalytics | null;
  loading: boolean;
  error: boolean;
  filters: ContractAnalyticsQuery;
  setFilters: (next: ContractAnalyticsQuery) => void;
  reload: () => Promise<void>;
  isEmpty: boolean;
}

/**
 * US5: fetch aggregate contract-signing analytics with optional method/date
 * filters. Refetches whenever the filters change.
 */
const useContractAnalytics = (
  initial: ContractAnalyticsQuery = {}
): UseContractAnalytics => {
  const [filters, setFilters] = useState<ContractAnalyticsQuery>(initial);
  const [data, setData] = useState<SigningAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Guards against out-of-order responses: rapid filter changes fire overlapping
  // requests, so only the most recent one is allowed to write state.
  const requestIdRef = useRef(0);

  const reload = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const res = await getContractAnalytics(filters);
      if (requestId !== requestIdRef.current) return; // superseded by a newer request
      setData(res);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to load contract analytics", e);
      setError(true);
      setData(null);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    reload();
  }, [reload]);

  const isEmpty = !loading && !error && (!data || data.total_contracts === 0);

  return { data, loading, error, filters, setFilters, reload, isEmpty };
};

export default useContractAnalytics;
